'use client'

import React, { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';
import Image from 'next/image';
import { motion, Variants } from 'framer-motion';

import * as LucideIcons from 'lucide-react';
import { NodeProject, DrupalImage } from '@/types/drupal';
import { localizedHref } from '@/lib/locale';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';

const PLATFORM_CONFIG: Record<string, { label: string; icon: keyof typeof LucideIcons }> = {
  web:     { label: 'Web',     icon: 'Globe' },
  ios:     { label: 'iOS',     icon: 'Smartphone' },
  android: { label: 'Android', icon: 'Smartphone' },
  mobile:  { label: 'Mobile',  icon: 'Smartphone' },
  api:     { label: 'API',     icon: 'Server' },
};

const translations = {
  en: {
    back: 'Back to Projects',
    about: 'About this project',
    ctaHeading: 'Interested in a similar solution?',
    ctaBody: "Let's discuss how we can build something like this for your business.",
    ctaButton: 'Get in Touch',
  },
  es: {
    back: 'Volver a Proyectos',
    about: 'Sobre este proyecto',
    ctaHeading: '¿Interesado en una solución similar?',
    ctaBody: 'Hablemos de cómo podemos construir algo así para tu negocio.',
    ctaButton: 'Contáctanos',
  },
} as const;

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] },
  },
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const fixDrupalImages = (html: string) => {
  const baseUrl = process.env.NEXT_PUBLIC_DRUPAL_BASE_URL || '';
  return html.replace(/src="\/sites\/default\/files\//g, `src="${baseUrl}/sites/default/files/`);
};

// ---------------------------------------------------------------------------
// Placeholder images (used when no Drupal images are available)
// ---------------------------------------------------------------------------
const PLACEHOLDER_IMAGES: { mediaImage: DrupalImage }[] = [
  { mediaImage: { url: 'https://placehold.co/800x500/E1E8EF/0A4492?text=Dashboard+Overview', alt: 'Dashboard Overview', width: 800, height: 500 } },
  { mediaImage: { url: 'https://placehold.co/800x500/E1E8EF/0A4492?text=Analytics+View', alt: 'Analytics View', width: 800, height: 500 } },
  { mediaImage: { url: 'https://placehold.co/800x500/E1E8EF/0A4492?text=Settings+Panel', alt: 'Settings Panel', width: 800, height: 500 } },
];

// ---------------------------------------------------------------------------
// Carousel
// ---------------------------------------------------------------------------

interface CarouselProps {
  images?: { mediaImage: DrupalImage }[];
  title: string;
}

function ProjectImageCarousel({ images, title }: CarouselProps) {
  const slides = images && images.length > 0 ? images : PLACEHOLDER_IMAGES;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const onReInit = useCallback(
    (api: EmblaCarouselType) => {
      setScrollSnaps(api.scrollSnapList());
      onSelect(api);
    },
    [onSelect],
  );

  useEffect(() => {
    if (!emblaApi) return;

    queueMicrotask(() => onReInit(emblaApi));
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onReInit);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onReInit);
    };
  }, [emblaApi, onSelect, onReInit]);

  return (
    <div className="flex flex-col items-center">
      <div
        className="relative w-full overflow-hidden rounded-xl"
        ref={emblaRef}
        role="region"
        aria-roledescription="carousel"
        aria-label={`${title} images`}
      >
        <div className="flex">
          {slides.map((img, idx) => (
            <div
              key={idx}
              role="group"
              aria-roledescription="slide"
              aria-label={`Image ${idx + 1} of ${slides.length}`}
              className="flex-[0_0_100%] min-w-0"
            >
              <div className="relative aspect-[16/10] shadow-lg rounded-xl overflow-hidden bg-[#E1E8EF]">
                <Image
                  src={img.mediaImage.url}
                  alt={img.mediaImage.alt || `${title} screenshot`}
                  fill
                  className="object-contain"
                  sizes="(max-width: 1024px) 95vw, 55vw"
                  unoptimized={img.mediaImage.url.includes('placehold.co')}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div aria-live="polite" aria-atomic="true" className="sr-only">
        Image {selectedIndex + 1} of {scrollSnaps.length}
      </div>

      {slides.length > 1 && (
        <div className="mt-6 flex items-center justify-center gap-4 w-full max-w-[440px] mx-auto">
          <button
            aria-label="Previous image"
            disabled={!canScrollPrev}
            className={`transition-colors duration-200 ${canScrollPrev ? 'cursor-pointer' : 'opacity-30 cursor-not-allowed'}`}
            onClick={() => emblaApi?.scrollPrev()}
          >
            <Icon name="ChevronLeft" size={32} color="#0A4492" />
          </button>

          <div
            className="relative flex-1 h-12 bg-[#FDFDFD] rounded-full border border-[#C2C2C2] flex items-center px-[22px]"
            role="group"
            aria-label={`Image ${selectedIndex + 1} of ${scrollSnaps.length}`}
          >
            <div className="flex w-full gap-2 items-center">
              {scrollSnaps.map((_, index) => (
                <div
                  key={index}
                  className="relative flex-1 h-0.5 bg-[#E0E0E0] rounded-full overflow-hidden"
                  aria-hidden="true"
                >
                  <motion.div
                    initial={false}
                    animate={{ x: index === selectedIndex ? '0%' : '-100%' }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className={`
                      absolute inset-0 bg-[#3CABE2] transition-transform duration-500 ease-out
                      ${index === selectedIndex ? 'translate-x-0' : '-translate-x-full'}
                    `}
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            aria-label="Next image"
            disabled={!canScrollNext}
            className={`transition-colors duration-200 ${canScrollNext ? 'cursor-pointer' : 'opacity-30 cursor-not-allowed'}`}
            onClick={() => emblaApi?.scrollNext()}
          >
            <Icon name="ChevronRight" size={32} color="#0A4492" />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProjectDetail
// ---------------------------------------------------------------------------

interface ProjectDetailProps {
  project: NodeProject;
  lang: string;
}

export default function ProjectDetail({ project, lang }: ProjectDetailProps) {
  const i18n = translations[lang as keyof typeof translations] ?? translations.en;
  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="ProjectDetail overflow-hidden"
      aria-label="Project Detail"
    >
      {/* Hero area */}
      <div className="bg-[#E1E8EF]">
        <div className="site-container lg:border-l lg:border-r lg:border-[#D9D9D9]">
          <div className="section-container py-[40px] lg:py-[80px]">
            <motion.div variants={fadeInUp}>
              <Button
                label={i18n.back}
                href={localizedHref(lang, '/#projects')}
                variant="ghost"
                iconLeft="ArrowLeft"
              />
            </motion.div>

            <div className="mt-8 lg:mt-12">
              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-2 mb-4">
                {project.projectType?.name && (
                  <span className="inline-block border border-[#0A4492] text-[#0A4492] text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                    {project.projectType.name}
                  </span>
                )}
                {project.platforms?.map((value) => {
                  const cfg = PLATFORM_CONFIG[value];
                  if (!cfg) return null;
                  return (
                    <span
                      key={value}
                      className="inline-flex items-center gap-1.5 border border-[#0A4492]/25 bg-white/60 text-[#0A4492] text-xs font-medium px-3 py-1 rounded-full"
                    >
                      <Icon name={cfg.icon} size={12} color="#0A4492" />
                      {cfg.label}
                    </span>
                  );
                })}
              </motion.div>

              <motion.h1
                variants={fadeInUp}
                className="text-[#0A4492] text-[32px] lg:text-[56px] font-semibold leading-tight"
              >
                {project.title}
              </motion.h1>

              {project.description?.processed && (
                <motion.div
                  variants={fadeInUp}
                  className="text-[#0A4492]/70 text-base lg:text-xl max-w-[700px] mt-4 lg:mt-6"
                  dangerouslySetInnerHTML={{ __html: project.description.processed }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gallery section */}
      <div className="bg-white">
        <div className="site-container lg:border-l lg:border-r lg:border-[#D9D9D9]">
          <div className="section-container py-[40px] lg:py-[80px]">
            <motion.div variants={fadeInUp}>
              <ProjectImageCarousel images={project.images} title={project.title} />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Description section */}
      {project.body?.processed && (
        <div className="bg-[#F8F9FB]">
          <div className="site-container lg:border-l lg:border-r lg:border-[#D9D9D9]">
            <div className="section-container py-[40px] lg:py-[80px]">
              <motion.div
                variants={fadeInUp}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16"
              >
                <div className="lg:col-span-4">
                  <h2 className="text-[#0A4492] text-[22px] lg:text-[32px] font-semibold leading-tight">
                    {i18n.about}
                  </h2>
                </div>
                <div className="lg:col-span-8 overflow-x-auto">
                  <div
                    className="
                      prose prose-blue max-w-none
                      [&>*:first-child]:mt-0
                      [&_h2]:text-[#0A4492] [&_h2]:text-[20px] [&_h2]:lg:text-[28px] [&_h2]:font-semibold [&_h2]:leading-tight [&_h2]:mt-10 [&_h2]:mb-4
                      [&_h3]:text-[#0A4492] [&_h3]:text-[17px] [&_h3]:lg:text-[22px] [&_h3]:font-semibold [&_h3]:leading-tight [&_h3]:mt-8 [&_h3]:mb-3
                      [&_h4]:text-[#0A4492] [&_h4]:text-[15px] [&_h4]:lg:text-[18px] [&_h4]:font-semibold [&_h4]:mt-6 [&_h4]:mb-2
                      [&_p]:text-[#4A5568] [&_p]:text-[14px] [&_p]:lg:text-[17px] [&_p]:leading-[1.7] [&_p]:mb-5
                      [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6
                      [&_li]:text-[#4A5568] [&_li]:text-[14px] [&_li]:lg:text-[17px] [&_li]:leading-[1.65] [&_li]:mb-2
                      [&_strong]:text-[#0A4492] [&_strong]:font-semibold
                      [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_table]:mb-8 [&_table]:min-w-[500px]
                      [&_thead]:bg-[#0A4492]
                      [&_th]:text-left [&_th]:text-white [&_th]:font-semibold [&_th]:py-3 [&_th]:px-4 [&_th]:text-[12px] [&_th]:lg:text-[13px] [&_th]:whitespace-nowrap
                      [&_td]:py-2.5 [&_td]:px-4 [&_td]:border-b [&_td]:border-[#E2E8F0] [&_td]:text-[#4A5568] [&_td]:text-[12px] [&_td]:lg:text-[14px] [&_td]:align-top
                      [&_tbody_tr:nth-child(even)]:bg-[#F0F4F8] [&_tbody_tr:nth-child(odd)]:bg-white [&_tbody_tr:hover]:bg-[#E1E8EF]
                    "
                    dangerouslySetInnerHTML={{
                      __html: fixDrupalImages(project.body.processed),
                    }}
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="bg-[#002E84]">
        <div className="site-container">
          <div className="section-container py-[40px] lg:py-[60px] text-center">
            <motion.div variants={fadeInUp}>
              <h2 className="text-white text-[22px] lg:text-[36px] font-semibold mb-4">
                {i18n.ctaHeading}
              </h2>
              <p className="text-white/70 text-sm lg:text-lg mb-8 max-w-lg mx-auto">
                {i18n.ctaBody}
              </p>
              <Button
                label={i18n.ctaButton}
                href={localizedHref(lang, '/#contact')}
                variant="secondary"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
